import getMaterialKey from './materials/get-material-key.js';
import updateMaterial from './materials/update-material.js';
import {
  getMaterialsByMetadata,
  getMeshesByMetadata,
} from './metadata-cache.js';
import { setModelState } from './state.js';
import type {
  MaterialConfig,
  MetadataRule,
  MetadataRules,
  Ref,
  State,
} from './types.js';

const applyRulesetToMaterial = (
  materialConfigs: Record<string, MaterialConfig>,
  materialId: string,
  ruleset: MetadataRule,
): void => {
  materialConfigs[materialId] = {
    ...materialConfigs[materialId],
    ...ruleset,
  };
};

const applySpectacleVisibilityFilter = (
  stateRef: Ref<State>,
  modelId: string,
  currentConfig: Record<string, string | boolean>,
  hardProperties: string[] = [],
): void => {
  const state = stateRef.current;
  const modelState = state.models[modelId];

  if (!modelState?.metadataCache) {
    return;
  }

  const { metadataCache, allMeshes } = modelState;
  const { visibilityProps } = metadataCache;

  const visibilityDecisions = new Map<string, boolean>();

  for (const [meshId, meshVisibilityProps] of visibilityProps.entries()) {
    let isVisible = true;

    for (const [configProperty, isActive] of Object.entries(currentConfig)) {
      if (
        isActive === true &&
        configProperty in meshVisibilityProps &&
        !meshVisibilityProps[configProperty]
      ) {
        isVisible = false;
        break;
      }
    }

    if (isVisible && hardProperties.length > 0) {
      const activeHardProperties = hardProperties.filter((property) =>
        Boolean(currentConfig[property]),
      );
      const meshHardProperties = hardProperties.filter(
        (property) => meshVisibilityProps[property] === true,
      );

      if (meshHardProperties.length > 0) {
        isVisible =
          activeHardProperties.length > 0 ?
            activeHardProperties.some((property) =>
              meshHardProperties.includes(property),
            )
          : false;
      }
    }

    visibilityDecisions.set(meshId, isVisible);
  }

  for (const mesh of allMeshes) {
    const targetVisibility = visibilityDecisions.get(mesh.id);
    if (
      targetVisibility !== undefined &&
      mesh.isEnabled(false) !== targetVisibility
    ) {
      mesh.setEnabled(targetVisibility);
    }
  }
};

const applyMetadataRules = async (
  stateRef: Ref<State>,
  modelId: string,
  metadataRules: MetadataRules,
): Promise<void> => {
  const state = stateRef.current;
  const modelState = state.models[modelId];
  const { scene } = state;

  if (!modelState?.metadataCache || !scene) {
    return;
  }

  const { metadataCache, allMeshes } = modelState;
  const materialConfigs: Record<string, MaterialConfig> = {};
  const visibilityDecisions = new Map<string, boolean>();

  for (const [metadataKey, valueRules] of Object.entries(metadataRules)) {
    for (const [metadataValue, ruleset] of Object.entries(valueRules)) {
      const matchingMeshIds = getMeshesByMetadata(
        metadataCache,
        metadataKey,
        metadataValue,
      );

      if (ruleset.isVisible !== undefined) {
        for (const meshId of matchingMeshIds) {
          visibilityDecisions.set(meshId, ruleset.isVisible);
        }
      }

      for (const meshId of matchingMeshIds) {
        const targetMeshes = allMeshes.filter(
          (m) => m.id === meshId || m.id.startsWith(`${meshId}_primitive`),
        );
        for (const mesh of targetMeshes) {
          if (mesh.material) {
            applyRulesetToMaterial(materialConfigs, mesh.material.id, ruleset);
          }
        }
      }

      const matchingMaterialIds = getMaterialsByMetadata(
        metadataCache,
        metadataKey,
        metadataValue,
      );
      for (const materialId of matchingMaterialIds) {
        const material = scene.materials.find((m) => m.id === materialId);
        if (material) {
          applyRulesetToMaterial(materialConfigs, materialId, ruleset);
        }
      }
    }
  }

  for (const mesh of allMeshes) {
    const targetVisibility = visibilityDecisions.get(mesh.id);
    if (
      targetVisibility !== undefined &&
      mesh.isEnabled(false) !== targetVisibility
    ) {
      mesh.setEnabled(targetVisibility);
    }
  }

  const materialKeys: Record<string, string> = {};
  const materialsToUpdate: [string, MaterialConfig][] = [];

  for (const [materialId, materialConfig] of Object.entries(materialConfigs)) {
    const currentKey = getMaterialKey(materialConfig);
    materialKeys[materialId] = currentKey;

    const storedKey = modelState.metadataRuleMaterialKeys?.[materialId];
    if (currentKey !== storedKey) {
      materialsToUpdate.push([materialId, materialConfig]);
    }
  }

  if (materialsToUpdate.length > 0) {
    const missingMaterials: string[] = [];
    await Promise.all(
      materialsToUpdate.map(async ([materialId, materialConfig]) =>
        updateMaterial(stateRef, missingMaterials)(
          modelId,
          materialId,
          materialConfig,
        ),
      ),
    );

    const updatedState = stateRef.current;
    const updatedModelState = updatedState.models[modelId];
    if (updatedModelState) {
      setModelState(stateRef)(modelId, {
        ...updatedModelState,
        metadataRuleMaterialKeys: {
          ...updatedModelState.metadataRuleMaterialKeys,
          ...materialKeys,
        },
      });
    }
  }
};

export { applyMetadataRules, applySpectacleVisibilityFilter };
